<script lang="ts">
	import DatePickerBox from './DatePickerBox.svelte'
	import DatePickerPresets from './DatePickerPresets.svelte'
	import MonthGrid from './MonthGrid.svelte'
	import {
		type Preset,
		type Presets,
		getCurrentDateTime,
		getHumanReadableDate,
		getHumanReadableDateTime,
		getWeeks,
	} from './datetime'
	import { _ } from '@repo/i18n'
	import { Dropdown } from '@repo/ui'
	import { Label } from '@repo/ui'
	import { Icon } from '@repo/ui'
	import { ChevronUpDown } from '@steeze-ui/heroicons'
	import type { DateTime } from 'luxon'
	import { createEventDispatcher } from 'svelte'

	interface Props {
		value?: DateTime
		label?: string
		placeholder?: string
		required?: boolean
		disabled?: boolean
		allowedPresets?: Presets[]
		formatAsDate?: boolean
		full?: boolean
		min?: DateTime
		max?: DateTime
	}

	let {
		value = $bindable(null),
		label = '',
		placeholder: placeholderText = $_('datepicker.selectTime'),
		required = false,
		disabled = false,
		allowedPresets = [],
		formatAsDate = false,
		full = false,
		min = null,
		max = null,
	}: Props = $props()

	let imaginaryEnd: DateTime = $state(null)

	let isDatePickerOpen = $state(false)

	let endMonth = $state((value ? value : getCurrentDateTime()).startOf('month'))
	let startMonth = $state(
		value
			? value.startOf('month').minus({ months: 1 })
			: getCurrentDateTime().startOf('month').minus({ months: 1 }),
	)

	const dispatch = createEventDispatcher()

	function handleDateSelected() {
		isDatePickerOpen = false
		dispatch('selected', {
			value,
		})
	}

	function handlePresetSelected(preset: Preset) {
		value = preset.end() || null

		handleDateSelected()
	}

	function handleMonthChange(event: CustomEvent<number>) {
		startMonth = startMonth.plus({ month: event.detail })
		endMonth = endMonth.plus({ month: event.detail })
	}

	function formatDateTimeForInput(dateTime: DateTime): string {
		return dateTime ? dateTime.toFormat("yyyy-MM-dd'T'T") : ''
	}
</script>

<div
	class="relative {isDatePickerOpen ? 'z-10' : ''}"
	class:pointer-events-none={disabled}
	class:w-full={full}
>
	{#if label}
		<Label name="date-picker" {label} {required} />
	{/if}

	<Dropdown portal bind:opened={isDatePickerOpen}>
		{#snippet placeholder({ onclick })}
			<input
				class="pointer-events-none absolute top-0 right-0 bottom-0 left-0 opacity-0"
				{disabled}
				max={formatDateTimeForInput(max)}
				min={formatDateTimeForInput(min)}
				{required}
				type="datetime-local"
				value={formatDateTimeForInput(value)}
			/>

			<button {onclick} id="date-picker" class="relative w-full" {disabled} type="button">
				<span class="pointer-events-none absolute right-0.5 flex h-full items-center text-gray-400">
					<Icon src={ChevronUpDown} />
				</span>
				<span
					class="input-colors shadow-input flex w-full appearance-none flex-wrap items-center gap-1 rounded-md border px-3 py-2 text-base"
				>
					<span class:text-gray-400={!Boolean(value)}>
						{#if value}
							{formatAsDate ? getHumanReadableDate(value) : getHumanReadableDateTime(value)}
						{:else if placeholderText}
							{placeholderText}
						{/if}
					</span>
				</span>
			</button>
		{/snippet}

		<DatePickerBox>
			<div class="grid grid-cols-8">
				<div class="order-2 col-span-8 p-4">
					<MonthGrid
						month={endMonth}
						weeks={getWeeks(endMonth)}
						bind:end={value}
						bind:imaginaryEnd
						on:monthChange={handleMonthChange}
						on:selected={() => {
							handleDateSelected()
						}}
					/>
				</div>

				{#if allowedPresets.length > 0}
					<div
						class="order-1 col-span-8 flex flex-1 flex-col justify-between gap-4 dark:border-l-gray-700"
					>
						<DatePickerPresets
							{allowedPresets}
							to={value}
							onPresetSelected={handlePresetSelected}
						/>
					</div>
				{/if}
			</div>
		</DatePickerBox>
	</Dropdown>
</div>
